import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api/client";
import {
  clientsUsagePath,
  submittersPath,
  type ClientsUsageResponse,
  type SubmitterRow,
  type SubmittersResponse,
} from "../api/usage";
import { Empty, ErrorState, Loading } from "../components/States";
import { NO_SUBMITTER } from "../components/SubmitterFilter";
import {
  Breadcrumb,
  CustomRange,
  SegmentedControl,
  Toolbar,
  type Segment,
} from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtCost, fmtDateTime, fmtTokens } from "../lib/format";

type WindowKey = "7" | "30" | "90" | "365" | "custom";

const WINDOWS: Segment<WindowKey>[] = [
  { key: "7", label: "Last 7d" },
  { key: "30", label: "Last 30d" },
  { key: "90", label: "Last 90d" },
  { key: "365", label: "Last year" },
  { key: "custom", label: "Custom" },
];

/** YYYY-MM-DD in local time, which is what a date input shows. */
const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  // Inclusive of today, so "last 7 days" is seven calendar days, not eight.
  from.setDate(to.getDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(to) };
}

/**
 * Who submitted how much, console and API alike.
 *
 * The document counts and cost come from documents.submitted_by_user_id,
 * written with the document. The request and error columns come from the API
 * request log, which only exists for client credentials and is metered
 * best-effort — so they are shown beside the document figures, never mixed
 * into them.
 */
export function Usage() {
  const [win, setWin] = useState<WindowKey>("30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range =
    win === "custom" ? { from: customFrom, to: customTo } : presetRange(Number(win));
  const ready = range.from !== "" && range.to !== "";

  const docs = useApi<SubmittersResponse>(
    ready ? submittersPath(range.from, range.to) : null,
  );
  const api = useApi<ClientsUsageResponse>(
    ready ? clientsUsagePath(range.from, range.to) : null,
  );

  const traffic = useMemo(
    () => new Map((api.data?.clients ?? []).map((c) => [c.client_id, c])),
    [api.data],
  );

  const rows = docs.data?.submitters ?? [];
  const totals = useMemo(() => {
    const priced = rows.filter((r) => r.cost_usd !== null);
    return {
      documents: rows.reduce((s, r) => s + r.documents, 0),
      accounts: rows.filter((r) => r.user_id).length,
      viaApi: rows.reduce((s, r) => s + r.via_api, 0),
      failed: rows.reduce((s, r) => s + r.failed, 0),
      cost: priced.length ? priced.reduce((s, r) => s + (r.cost_usd ?? 0), 0) : null,
      costComplete: rows.every((r) => r.cost_complete !== false),
    };
  }, [rows]);

  /** The document list, narrowed to this account and this window. */
  const documentsLink = (r: SubmitterRow) =>
    `/documents?${new URLSearchParams({
      submitted_by: r.user_id ?? NO_SUBMITTER,
      period: "custom",
      date_from: range.from,
      date_to: range.to,
    }).toString()}`;

  const exportCsv = () => {
    if (!rows.length) return;
    downloadCsv(
      stampedName("usage-by-account"),
      toCsv<SubmitterRow>(rows, [
        { header: "Account", value: (r) => r.username ?? (r.user_id ? r.user_id : "not recorded") },
        { header: "Email", value: (r) => r.email },
        { header: "Role", value: (r) => r.role },
        { header: "Client ID", value: (r) => r.client_id },
        { header: "Documents", value: (r) => r.documents },
        { header: "Via API", value: (r) => r.via_api },
        { header: "Completed", value: (r) => r.completed },
        { header: "Failed", value: (r) => r.failed },
        { header: "In progress", value: (r) => r.in_progress },
        { header: "API requests", value: (r) => (r.client_id ? traffic.get(r.client_id)?.requests ?? 0 : null) },
        { header: "API errors", value: (r) => (r.client_id ? traffic.get(r.client_id)?.errors ?? 0 : null) },
        // Blank, not 0, when nothing was priced: unknown is not free.
        { header: "Cost USD", value: (r) => r.cost_usd },
        { header: "Cost complete", value: (r) => (r.cost_complete === false ? "no" : "yes") },
        { header: "First submitted", value: (r) => r.first_submitted_at },
        { header: "Last submitted", value: (r) => r.last_submitted_at },
      ]),
    );
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Usage" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Usage by account</h1>
          <p className="page-sub">
            Documents each console account and API client submitted, and what they cost.
          </p>
        </div>
        <div className="range-group">
          {win === "custom" ? (
            <CustomRange
              from={customFrom}
              to={customTo}
              onFrom={setCustomFrom}
              onTo={setCustomTo}
              idPrefix="usage"
            />
          ) : null}
          <SegmentedControl
            segments={WINDOWS}
            value={win}
            onChange={(w) => {
              setWin(w);
              if (w === "custom" && !customFrom && !customTo) {
                const r = presetRange(30);
                setCustomFrom(r.from);
                setCustomTo(r.to);
              }
            }}
            label="Time range"
          />
        </div>
      </div>

      {!ready ? (
        <div className="card">
          <Empty title="Pick both dates" hint="A custom range needs a start and an end." />
        </div>
      ) : docs.loading ? (
        <div className="card">
          <Loading what="usage" />
        </div>
      ) : docs.error ? (
        <div className="card">
          <ErrorState message={docs.error} onRetry={docs.reload} />
        </div>
      ) : (
        <>
          <div className="tiles">
            <div className="tile">
              <div className="tile-label">Documents</div>
              <div className="tile-value">{fmtTokens(totals.documents)}</div>
              <div className={`tile-note${totals.failed ? " warn" : ""}`}>
                {totals.failed ? `${totals.failed} failed` : "none failed"}
              </div>
            </div>
            <div className="tile">
              <div className="tile-label">Accounts</div>
              <div className="tile-value">{totals.accounts}</div>
              <div className="tile-note">submitted at least one</div>
            </div>
            <div className="tile">
              <div className="tile-label">Via API</div>
              <div className="tile-value">{fmtTokens(totals.viaApi)}</div>
              <div className="tile-note">
                {totals.documents
                  ? `${Math.round((totals.viaApi / totals.documents) * 100)}% of documents`
                  : EM_DASH}
              </div>
            </div>
            <div className="tile">
              <div className="tile-label">Cost</div>
              <div className="tile-value">{fmtCost(totals.cost)}</div>
              <div className={`tile-note${totals.costComplete ? "" : " warn"}`}>
                {totals.costComplete ? "all documents priced" : "lower bound — some unpriced"}
              </div>
            </div>
          </div>

          {api.error ? (
            <div className="notice">
              API request counts are unavailable ({api.error}). Document counts below are
              unaffected.
            </div>
          ) : null}

          <Toolbar
            title="Accounts"
            subtitle={`${docs.data?.from ?? range.from} to ${docs.data?.to ?? range.to} · select a count to open those documents`}
            onExport={exportCsv}
            exportDisabled={!rows.length}
          />

          <div className="card">
            {rows.length === 0 ? (
              <Empty
                title="Nothing submitted in this window"
                hint="Try a longer range."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Account</th>
                      <th scope="col" className="num">Documents</th>
                      <th scope="col" className="num">Completed</th>
                      <th scope="col" className="num">Failed</th>
                      <th scope="col" className="num">In progress</th>
                      <th
                        scope="col"
                        className="num"
                        title="Of this account's documents, how many came through the external API"
                      >
                        Via API
                      </th>
                      <th
                        scope="col"
                        className="num"
                        title="Requests made with this account's client credential, from the API request log. Includes polling and token exchanges, not only submissions."
                      >
                        API requests
                      </th>
                      <th scope="col" className="num">Cost</th>
                      <th scope="col">Last submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const t = r.client_id ? traffic.get(r.client_id) : undefined;
                      return (
                        <tr key={r.user_id ?? NO_SUBMITTER}>
                          <td>
                            {r.user_id ? (
                              <>
                                <div>
                                  <strong>{r.username ?? r.user_id.slice(0, 8)}</strong>
                                  {r.client_id ? (
                                    <>
                                      {" "}
                                      <span className="pill" title={`Client ID ${r.client_id}`}>
                                        API client
                                      </span>
                                    </>
                                  ) : null}
                                  {r.is_active === false ? (
                                    <>
                                      {" "}
                                      <span className="pill pill-critical">revoked</span>
                                    </>
                                  ) : null}
                                </div>
                                <div className="muted" style={{ fontSize: 12 }}>
                                  {[r.email, r.role].filter(Boolean).join(" · ")}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="muted">Not recorded</div>
                                <div className="muted" style={{ fontSize: 12 }}>
                                  .env accounts, or submitted before submitters were recorded
                                </div>
                              </>
                            )}
                          </td>
                          <td className="num">
                            <Link to={documentsLink(r)}>{fmtTokens(r.documents)}</Link>
                          </td>
                          <td className="num">{fmtTokens(r.completed)}</td>
                          <td className="num">
                            {r.failed ? (
                              <span className="req-bad">{fmtTokens(r.failed)}</span>
                            ) : (
                              0
                            )}
                          </td>
                          <td className="num">{fmtTokens(r.in_progress)}</td>
                          <td className="num">{r.via_api ? fmtTokens(r.via_api) : EM_DASH}</td>
                          <td className="num">
                            {!r.client_id ? (
                              <span className="muted" title="No client credential">
                                {EM_DASH}
                              </span>
                            ) : t ? (
                              <>
                                {fmtTokens(t.requests)}
                                {t.errors ? (
                                  <div className="muted" style={{ fontSize: 11 }}>
                                    {fmtTokens(t.errors)} errors
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              0
                            )}
                          </td>
                          <td className="num">
                            {fmtCost(r.cost_usd)}
                            {r.cost_complete === false ? (
                              <div
                                className="muted"
                                style={{ fontSize: 11 }}
                                title="Some of these documents have no usage recorded"
                              >
                                lower bound
                              </div>
                            ) : null}
                          </td>
                          <td className="nowrap muted">{fmtDateTime(r.last_submitted_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
